import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { catchError, defaultIfEmpty, firstValueFrom, map, of, Subscription, tap } from 'rxjs';
import { CustomizationService } from 'src/services/customization.service';
import { CustomizationStoreService } from 'src/services/customization.store.service';
import { TranslationService } from 'src/services/translation.service';
import { normalizeWidgetLang } from 'src/services/lang.utils';

@Component({
  selector: 'belender-terms',
  templateUrl: './terms.component.html',
  styleUrls: ['./terms.component.scss']
})
export class TermsComponent implements OnInit, OnChanges {
  loading = true;
  terms_and_conditions: string = ''; // Inicializar para evitar problemas
  funderConfig: any;
  funderStyles: any;
  flowConfig: any;

  // 1. INPUTS para recibir los datos del componente padre (reemplaza MAT_DIALOG_DATA)
  @Input() set allData(data: any | null) {
    if (data) {
      this.funderConfig = data.config;
      this.funderStyles = data.styles;
      // Asume que el flujo actual se pasa en la estructura de 'allData'
      this.flowConfig = data[this.flow || ''];
      this.setCustomization();
    }
  }

  @Input() flow: string | undefined;
  @Input() lang: string | null = 'es';
  @Input() requestID: string | null;
  @Input() requestStatus: string | null;
  // Contenido fijo (p.ej. hoja legal del certificado). Si viene, se muestra tal cual
  // y se omite la carga dinámica de T&C por flujo/funder.
  @Input() customTerms?: string;

  // 2. OUTPUTS para notificar al padre (reemplaza dialogRef.afterClosed())
  @Output() accepted = new EventEmitter<boolean>();
  @Output() cancelled = new EventEmitter<boolean>();


  texts = {
    terms_and_conditions: '',
    cancel: 'Cancelar',
    accept: 'Aceptar'
  }

  buttonsText = {
    it: { cancel: 'Anulla', accept: 'Accetta' },
    pt: { cancel: 'Cancelar', accept: 'Aceitar' },
    ca: { cancel: 'Cancel·lar', accept: 'Acceptar' },
    en: { cancel: 'Cancel', accept: 'Accept' },
    de: { cancel: 'Abbrechen', accept: 'Akzeptieren' },
    fr: { cancel: 'Annuler', accept: 'Accepter' },
  }
  sTerms: Subscription;


  constructor(
    private customizationService: CustomizationService,
    private customizationStoreService: CustomizationStoreService,
  ) {

    // set Customization data when CustomizationService request is completed
    this.customizationStoreService.getFunderStyles().subscribe(
      (styles) => {
        if (Object.keys(styles).length > 0) {
          this.funderStyles = styles;
        }
      },
    );

    this.customizationStoreService.getFunderConfig().subscribe(
      (config) => {
        if (Object.keys(config).length > 0) {
          this.funderConfig = config;
        }
      },
    );
  }

  ngOnInit(): void {
    this.parseButtons(this.resolveLang());
    if (this.customTerms) {
      this.texts.terms_and_conditions = this.customTerms;
      this.loading = false;
      return;
    }
    this.loading = true;
    if (this.funderConfig || this.funderStyles) {
      this.setCustomization();
    } else {
      this.customizationStoreService.getUserID().subscribe(uid => {
        this.customizationService.getCustomizationByFlow(
          uid,
          this.getParsedFlowName(this.flow!),
          this.customizationStoreService.LANG,
          this.requestID ?? undefined,
          this.requestStatus ?? undefined
        ).then((d: any) => {
          this.flowConfig = d.data;
          this.setCustomization();
        }).catch(e => {this.loading = false;})
      }, e => {this.loading = false;})
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.lang) {
      this.parseButtons(this.resolveLang());
    }
  }

  private resolveLang(): string {
    return normalizeWidgetLang(this.lang || localStorage.getItem('belender-language'));
  }

  // Métodos de acción para el template
  onAccept() {
    this.accepted.emit(true);
  }

  onCancel() {
    this.cancelled.emit(false);
  }

  parseButtons(lang: string) {
    const langKey = normalizeWidgetLang(lang);
    if (this.buttonsText[langKey]) {
      this.texts.accept = this.buttonsText[langKey].accept;
      this.texts.cancel = this.buttonsText[langKey].cancel;
    }
  }

  getRequestIdString() {
    if(this.requestStatus === 'prerequest') {
      if(this.flow && this.flow === 'SmsPin') {
        return 'prerequest_combined_flows_id';
      }
      return 'prerequest_id';
    }
    return 'request_id';
  }

  async getTerms() {
    return firstValueFrom(
      this.customizationStoreService.getTermsTranslated().pipe(
        map(terms => (terms && Object.keys(terms).length > 0 ? terms : null)),
        defaultIfEmpty(null),
        catchError((error) => of(null).pipe(
          tap(() => console.error(error)),
        )),
      )
    );
  }

  isObjectEmpty(obj: any): boolean {
    return !obj || !Object.keys(obj) || Object.keys(obj).length === 0;
  }

  async setCustomization() {
    if (this.customTerms) {
      this.texts.terms_and_conditions = this.customTerms;
      this.loading = false;
      return;
    }
    const terms: any = await this.getTerms();

    if(!this.isObjectEmpty(terms)) {
      this.texts.terms_and_conditions = terms;
      this.parseButtons(this.resolveLang());
      this.loading = false;
    } else if (this.requestID) {
      let reqIdString = this.getRequestIdString()
      this.customizationService.getFlowTerms(this.customizationStoreService.USER_ID, this.getParsedFlowName(this.flow!), this.requestID, reqIdString).then((data: any) => {
        if(data && data.data && data.data.terms) {
          this.texts.terms_and_conditions = data.data.terms;
          this.loading = false;
        } else {
          this.setTermsFronCustomizationTerms()
        }
      }).catch( () => {
        this.setTermsFronCustomizationTerms()
      })
    } else {
      this.setTermsFronCustomizationTerms()
    }

  }

  setTermsFronCustomizationTerms() {
    if(this.flowConfig && this.flowConfig.texts && this.flowConfig.texts.terms_and_conditions) {
      this.texts.terms_and_conditions = this.flowConfig.texts.terms_and_conditions;
    } else if(this.flowConfig && this.flowConfig.terms_and_conditions) {
      this.texts.terms_and_conditions = this.flowConfig.terms_and_conditions;
    } else {
      this.texts.terms_and_conditions = `
        <div class="px-10 py-6 rounded-md text-center bg-orange-500/20 text-orange-600">
          <p>No hay términos y condiciones para este flujo </p>
        </div>`;
    }
    this.parseButtons(this.resolveLang());
    this.loading = false;
  }

  getParsedFlowName(flow: string) {
    switch(flow) {
      case 'sms':
      case 'Sms':
        return 'SMS';
      case 'clavepin':
      case 'ClavePin':
      case 'CLAVEPIN':
        return 'ClavePin';
      case 'videoid':
        return 'VideoID';
      case 'personal':
      case 'presencial':
        return 'KnownClient';
      case 'client-cert':
          return 'KnownClient';
      case 'certificate':
        return 'ClientCertificate';
      default:
        return flow;
    }
  }
}
